/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { LogService } from '../../../../../core/services';

@Component({
    selector: 'app-image-upload',
    template: `
        <div class="image-upload-wrapper">
            <input #imageUpload [attr.accept]="'image/png, image/jpeg'" [id]="inputId" class="lb-upload" type="file"
                   (change)="onFileChange($event)"/>
            <label [for]="inputId" class="image-upload-button" [class.with-upload]="newFileSrc">
                <img id="company-logo" [src]="newFileSrc || '/assets/centralisedManagement/icons/camera.svg'"
                     alt="Uploaded company logo"/>
            </label>
        </div>
    `,
    styleUrls: ['./image-upload.component.scss']
})

export class ImageUploadComponent {
    /**
     * Get the imageUpload element.
     */
    @ViewChild('imageUpload', { static: true }) public input: any;

    /**
     * Pass in an id to associate a label.
     */
    @Input() public inputId: string = 'image-upload';

    /**
     * Allow the input to be reset externally
     */
    @Input() set resetFileInput(state) {
        this._resetFileInput = state;
        if (this._resetFileInput) {
            this.resetSelectedFile();
            this._resetFileInput = false;
        }
    }

    /**
     * When the file is change tell the parent component.
     */
    @Output() public fileSelected: EventEmitter<any> = new EventEmitter();

    /**
     * Store the file name of the selected file.
     */
    public fileName: string = '';

    /**
     * Image placeholder in upload container.
     */
    public placeholder: string = '/images/centralisedManagement/icons/camera.svg';

    /**
     * Used to set the image source when an image is uploaded.
     */
    public newFileSrc;

    /**
     * Store the reset state of the input.
     * @private
     */
    private _resetFileInput: boolean = false;

    constructor(
        private logService: LogService
    ) {
    }

    /**
     * When a file is selected tell the parent component.
     * @param event
     */
    public onFileChange(event: any): void {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            this.fileName = file.name;
            this.fileSelected.next(file.name);

            const reader = new FileReader();
            reader.onloadend = () => {
                this.newFileSrc = reader.result;
            };

            reader.readAsDataURL(file);
        }
    }

    /**
     * Reset the file input value.
     * @private
     */
    private resetSelectedFile(): void {
        this.fileName = '';
        this.input.nativeElement.value = '';
        this.logService.info('ImageUploadComponent:resetSelectedFile', 'Input type file reset');
    }
}
